import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* ─── Confetti particle ─── */
const Particle = ({ delay, color }: { delay: number; color: string }) => {
  const angle = Math.random() * 360;
  const dist  = 80 + Math.random() * 100;
  const tx = Math.cos((angle * Math.PI) / 180) * dist;
  const ty = Math.sin((angle * Math.PI) / 180) * dist;
  const size = 4 + Math.random() * 6;
  return (
    <motion.div
      initial={{ opacity: 1, x: 0, y: 0, scale: 1 }}
      animate={{ opacity: 0, x: tx, y: ty, scale: 0, rotate: angle * 2 }}
      transition={{ delay, duration: 0.9, ease: "easeOut" }}
      style={{
        position: "absolute", top: "50%", left: "50%",
        width: size, height: size, borderRadius: Math.random() > 0.5 ? "50%" : 2,
        background: color, pointerEvents: "none",
      }}
    />
  );
};

/* ─── SVG check-draw ─── */
const AnimatedCheck = () => (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
    <motion.circle
      cx="26" cy="26" r="24"
      stroke="#FF1C35" strokeWidth="2.5" fill="none"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 1 }}
      transition={{ duration: 0.55, ease: "easeOut" }}
    />
    <motion.path
      d="M14 26 L22 34 L38 18"
      stroke="#FF1C35" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ delay: 0.45, duration: 0.4, ease: "easeOut" }}
    />
  </svg>
);

/* ─── Success overlay inside form card ─── */
const SuccessPanel = ({ onReset }: { onReset: () => void }) => {
  const COLORS = ["#FF1C35","#ff6b6b","#fff","#FF1C35","#ffaa00","#fff","#FF1C35"];
  const particles = Array.from({ length: 22 }, (_, i) => ({
    delay: i * 0.03,
    color: COLORS[i % COLORS.length],
  }));
  return (
    <motion.div
      key="success"
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", textAlign: "center",
        padding: "52px 32px", minHeight: 400, position: "relative",
      }}
    >
      {/* Glow ring behind check */}
      <div style={{
        position: "relative", marginBottom: 28,
        filter: "drop-shadow(0 0 18px rgba(255,28,53,0.55))",
      }}>
        <AnimatedCheck />
        {/* Confetti burst */}
        {particles.map((p, i) => <Particle key={i} delay={p.delay} color={p.color} />)}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55, duration: 0.45 }}
      >
        <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", marginBottom: 10, letterSpacing: 0.3 }}>
          Message Sent!
        </div>
        <div style={{ fontSize: 14, color: "rgba(255,255,255,0.45)", lineHeight: 1.8, marginBottom: 6 }}>
          Thank you for reaching out to MIRE Media.
        </div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.25)", lineHeight: 1.7, marginBottom: 36 }}>
          感谢您的联系，我们将在 1–2 个工作日内回复您。
        </div>

        {/* Divider */}
        <div style={{
          width: 48, height: 2,
          background: "linear-gradient(90deg,transparent,#FF1C35,transparent)",
          margin: "0 auto 28px",
        }} />

        {/* Send another */}
        <motion.button
          whileHover={{ scale: 1.04 } as never}
          whileTap={{ scale: 0.97 } as never}
          onClick={onReset}
          style={{
            padding: "10px 28px", borderRadius: 8,
            background: "transparent",
            border: "1px solid rgba(255,28,53,0.4)",
            color: "#FF1C35", fontSize: 13, fontWeight: 700,
            cursor: "pointer", letterSpacing: 0.5,
          }}
        >
          Send Another Message
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

const ContactFooter = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending]   = useState(false);
  const [fields, setFields] = useState({ name: "", company: "", email: "", type: "", message: "" });

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const handleChange = (k: string, v: string) => setFields(f => ({ ...f, [k]: v }));

  const handleSubmit = () => {
    if (!fields.name || !fields.email) return;
    setSending(true);
    setTimeout(() => { setSending(false); setSubmitted(true); }, 1200);
  };

  const handleReset = () => {
    setFields({ name: "", company: "", email: "", type: "", message: "" });
    setSubmitted(false);
  };

  return (
    <>
      {/* Contact Section */}
      <section id="contact" ref={ref} style={{
        padding: "100px 40px",
        borderTop: "1px solid rgba(255,255,255,0.05)",
        background: "linear-gradient(180deg, #080808 0%, rgba(255,28,53,0.04) 100%)",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={visible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 36, height: 2, background: "#FF1C35" }} />
              <span style={{ color: "#FF1C35", fontSize: 12, fontWeight: 700, letterSpacing: 3 }}>GET IN TOUCH · 联系我们</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "start" }} className="contact-grid">
              {/* Left */}
              <div>
                <h2 style={{ fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 900, lineHeight: 1.1, margin: "0 0 20px" }}>
                  Ready to Build the<br /><span style={{ color: "#FF1C35" }}>Future of Entertainment</span><br />Together?
                </h2>
                <p style={{ fontSize: 15, color: "rgba(255,255,255,0.5)", lineHeight: 1.8, marginBottom: 36 }}>
                  Whether you're a brand partner, content distributor, IP developer, or media professional — we'd love to hear from you.
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                  {[
                    { icon: "📍", label: "Headquarters", value: "Kuala Lumpur, Malaysia" },
                    { icon: "🌐", label: "Global Offices", value: "London · Singapore · New York" },
                    { icon: "✉️", label: "General Inquiries", value: "info@miremedia.com" },
                    { icon: "🤝", label: "Partnership", value: "partner@miremedia.com" },
                  ].map(c => (
                    <div key={c.label} style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                      <span style={{ fontSize: 20, marginTop: 2 }}>{c.icon}</span>
                      <div>
                        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", fontWeight: 600, letterSpacing: 1 }}>{c.label.toUpperCase()}</div>
                        <div style={{ fontSize: 15, color: "rgba(255,255,255,0.8)", marginTop: 2 }}>{c.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Form */}
              <div style={{
                padding: "36px 32px", borderRadius: 20,
                background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                overflow: "hidden", position: "relative", minHeight: 420,
              }}>
                <AnimatePresence mode="wait">
                  {submitted ? (
                    <SuccessPanel key="success" onReset={handleReset} />
                  ) : (
                    <motion.div
                      key="form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      transition={{ duration: 0.3 }}
                    >
                      <h3 style={{ fontSize: 20, fontWeight: 800, color: "#fff", margin: "0 0 24px" }}>Send us a message</h3>
                      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        {[
                          { id: "name",    label: "Name",    type: "text",  placeholder: "Your full name" },
                          { id: "company", label: "Company", type: "text",  placeholder: "Company / Organization" },
                          { id: "email",   label: "Email",   type: "email", placeholder: "your@email.com" },
                        ].map(f => (
                          <div key={f.id}>
                            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.5)", marginBottom: 6, letterSpacing: 0.5 }}>{f.label.toUpperCase()}</label>
                            <input
                              type={f.type}
                              placeholder={f.placeholder}
                              value={fields[f.id as keyof typeof fields]}
                              onChange={e => handleChange(f.id, e.target.value)}
                              style={{
                                width: "100%", padding: "11px 14px", borderRadius: 8,
                                background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
                                color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box",
                                transition: "border-color 0.2s",
                              }}
                              onFocus={e => (e.currentTarget.style.borderColor = "rgba(255,28,53,0.5)")}
                              onBlur={e  => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)")}
                            />
                          </div>
                        ))}
                        <div>
                          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.5)", marginBottom: 6, letterSpacing: 0.5 }}>PARTNERSHIP TYPE</label>
                          <select
                            value={fields.type}
                            onChange={e => handleChange("type", e.target.value)}
                            style={{
                              width: "100%", padding: "11px 14px", borderRadius: 8,
                              background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
                              color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box",
                            }}
                          >
                            <option value="">Select partnership type...</option>
                            <option value="advertising">Advertising &amp; Brand Integration</option>
                            <option value="distribution">Content Distribution</option>
                            <option value="ip">IP Licensing</option>
                            <option value="investment">Investment</option>
                            <option value="media">Media / Press</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.5)", marginBottom: 6, letterSpacing: 0.5 }}>MESSAGE</label>
                          <textarea
                            rows={4}
                            placeholder="Tell us about your partnership vision..."
                            value={fields.message}
                            onChange={e => handleChange("message", e.target.value)}
                            style={{
                              width: "100%", padding: "11px 14px", borderRadius: 8,
                              background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
                              color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box",
                              resize: "vertical", fontFamily: "inherit",
                              transition: "border-color 0.2s",
                            }}
                            onFocus={e => (e.currentTarget.style.borderColor = "rgba(255,28,53,0.5)")}
                            onBlur={e  => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)")}
                          />
                        </div>
                        <motion.button
                          whileHover={!sending ? { scale: 1.02 } as never : {}}
                          whileTap={!sending  ? { scale: 0.97 } as never : {}}
                          onClick={handleSubmit}
                          disabled={sending}
                          style={{
                            padding: "13px 24px",
                            background: sending ? "rgba(255,28,53,0.5)" : "#FF1C35",
                            color: "#fff", border: "none", borderRadius: 8,
                            fontSize: 15, fontWeight: 700,
                            cursor: sending ? "not-allowed" : "pointer",
                            width: "100%", transition: "all 0.25s",
                            boxShadow: sending ? "none" : "0 0 24px rgba(255,28,53,0.25)",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                          }}
                        >
                          {sending ? (
                            <>
                              <motion.span
                                animate={{ rotate: 360 }}
                                transition={{ repeat: Infinity, duration: 0.75, ease: "linear" }}
                                style={{ display: "inline-block", width: 16, height: 16, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%" }}
                              />
                              Sending...
                            </>
                          ) : (
                            "Send Message →"
                          )}
                        </motion.button>

                        {/* Required field hint */}
                        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.2)", textAlign: "center", marginTop: -4 }}>
                          * Name and Email are required · 姓名和邮箱为必填项
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        borderTop: "1px solid rgba(255,255,255,0.07)",
        padding: "48px 40px 32px",
        background: "#060606",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 40, marginBottom: 48 }} className="footer-grid">
            {/* Brand */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <span style={{ color: "#FF1C35", fontWeight: 900, fontSize: 22, letterSpacing: 2 }}>MIRE</span>
                <span style={{ color: "rgba(255,255,255,0.3)", fontWeight: 400, fontSize: 12, letterSpacing: 3 }}>MEDIA</span>
              </div>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.35)", lineHeight: 1.8, maxWidth: 280, margin: "0 0 20px" }}>
                Next Generation AI Entertainment Platform. Headquartered in Kuala Lumpur, building Southeast Asia's digital content future.
              </p>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.2)", margin: 0, lineHeight: 1.7 }}>
                下一代AI娱乐平台 · 吉隆坡总部<br />东南亚最大本地短剧内容平台
              </p>
            </div>
            {/* Products */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 2, marginBottom: 16 }}>PRODUCTS</div>
              {["MIRE DRAMA", "ALOHA IP", "AI Production", "Distribution"].map(l => (
                <div key={l} style={{ padding: "6px 0", fontSize: 13, color: "rgba(255,255,255,0.5)", cursor: "pointer" }}
                  onMouseEnter={e => (e.currentTarget.style.color = "#FF1C35")}
                  onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.5)")}
                >{l}</div>
              ))}
            </div>
            {/* Company */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 2, marginBottom: 16 }}>COMPANY</div>
              {["About MIRE", "Our Team", "Careers", "Press Kit", "Investors"].map(l => (
                <div key={l} style={{ padding: "6px 0", fontSize: 13, color: "rgba(255,255,255,0.5)", cursor: "pointer" }}
                  onMouseEnter={e => (e.currentTarget.style.color = "#FF1C35")}
                  onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.5)")}
                >{l}</div>
              ))}
            </div>
            {/* Markets */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 2, marginBottom: 16 }}>MARKETS</div>
              {["🇲🇾 Malaysia", "🇮🇩 Indonesia", "🌏 Southeast Asia", "🇸🇬 Singapore", "🇬🇧 London"].map(l => (
                <div key={l} style={{ padding: "6px 0", fontSize: 13, color: "rgba(255,255,255,0.5)" }}>{l}</div>
              ))}
            </div>
          </div>

          <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.25)" }}>© 2026 MIRE Media. All rights reserved. · 版权所有</span>
            <div style={{ display: "flex", gap: 24 }}>
              {["Privacy Policy", "Terms of Service", "Cookie Policy"].map(l => (
                <span key={l} style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", cursor: "pointer" }}
                  onMouseEnter={e => (e.currentTarget.style.color = "#FF1C35")}
                  onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.3)")}
                >{l}</span>
              ))}
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @media (max-width: 768px) {
          .contact-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .footer-grid { grid-template-columns: 1fr 1fr !important; }
        }
        select option { background: #1a1a1a; color: #fff; }
      `}</style>
    </>
  );
};

export default ContactFooter;
